import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';
import MetricCard from './components/MetricCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import SalesChart from './components/SalesChart';
import InventoryStatus from './components/InventoryStatus';
import Icon from '../../components/AppIcon';

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userRole] = useState('manager'); // Mock user role - in real app from auth context

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Mock metrics data - in real app would come from API
  const metrics = [
    {
      title: 'Presupuestos Pendientes',
      value: '23',
      change: '+12%',
      changeType: 'positive',
      icon: 'FileText',
      color: 'primary'
    },
    {
      title: 'Ingresos del Mes',
      value: '€85.420',
      change: '+8,5%',
      changeType: 'positive',
      icon: 'Euro',
      color: 'success'
    },
    {
      title: 'Productos con Stock Bajo',
      value: '7',
      change: '+2',
      changeType: 'negative',
      icon: 'AlertTriangle',
      color: 'warning'
    },
    {
      title: 'Entregas Pendientes',
      value: '15',
      change: '-3',
      changeType: 'positive',
      icon: 'Truck',
      color: 'info'
    }
  ];

  const formatDateTime = (date) => {
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getRoleBasedGreeting = () => {
    const hour = currentTime.getHours();
    let timeGreeting = '';
    
    if (hour < 12) timeGreeting = 'Buenos días';
    else if (hour < 18) timeGreeting = 'Buenas tardes';
    else timeGreeting = 'Buenas noches';

    const roleGreetings = {
      manager: 'Bienvenido al panel de control',
      sales: 'Listo para generar nuevos presupuestos',
      warehouse: 'Control de inventario actualizado',
      accounting: 'Gestión financiera centralizada'
    };

    return {
      timeGreeting,
      roleGreeting: roleGreetings[userRole] || roleGreetings.manager
    };
  };

  const greeting = getRoleBasedGreeting();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Welcome Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-heading-semibold text-text-primary mb-2">
                  {greeting.timeGreeting}, María
                </h1>
                <p className="text-text-secondary font-body-normal mb-1">
                  {greeting.roleGreeting}
                </p>
                <p className="text-sm text-text-muted">
                  {formatDateTime(currentTime)}
                </p>
              </div>
              
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <div className="hidden lg:flex items-center space-x-2 px-4 py-2 bg-success-50 text-success rounded-lg border border-success-100">
                  <Icon name="Wifi" size={16} />
                  <span className="text-sm font-body-medium">Sistema Online</span>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics.map((metric, index) => (
              <MetricCard
                key={index}
                title={metric.title}
                value={metric.value}
                change={metric.change}
                changeType={metric.changeType}
                icon={metric.icon}
                color={metric.color}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Left Column - Charts */}
            <div className="lg:col-span-2 space-y-8">
              <SalesChart />
              <InventoryStatus />
            </div>

            {/* Right Column - Activity & Actions */}
            <div className="space-y-8">
              <ActivityFeed />
              <QuickActions />
            </div>
          </div>

          {/* Additional Insights for Managers */}
          {userRole === 'manager' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading-semibold text-text-primary">
                    Rendimiento del Equipo
                  </h3>
                  <Icon name="Users" size={20} className="text-primary" />
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-text-secondary">Ventas completadas</span>
                    <span className="text-sm font-body-medium text-text-primary">47</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-text-secondary">Presupuestos creados</span>
                    <span className="text-sm font-body-medium text-text-primary">89</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-text-secondary">Productos agregados</span>
                    <span className="text-sm font-body-medium text-text-primary">12</span>
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading-semibold text-text-primary">
                    Objetivos del Mes
                  </h3>
                  <Icon name="Target" size={20} className="text-success" />
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-text-secondary">Ingresos</span>
                      <span className="text-sm font-body-medium text-text-primary">85%</span>
                    </div>
                    <div className="w-full bg-secondary-100 rounded-full h-2">
                      <div className="bg-success h-2 rounded-full" style={{ width: '85%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-text-secondary">Nuevos Clientes</span>
                      <span className="text-sm font-body-medium text-text-primary">62%</span>
                    </div>
                    <div className="w-full bg-secondary-100 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '62%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading-semibold text-text-primary">
                    Próximas Tareas
                  </h3>
                  <Icon name="Calendar" size={20} className="text-warning" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-secondary-50">
                    <div className="w-2 h-2 bg-error rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-body-medium text-text-primary">
                        Revisar presupuesto urgente
                      </p>
                      <p className="text-xs text-text-muted">Hoy, 16:00</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-secondary-50">
                    <div className="w-2 h-2 bg-warning rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-body-medium text-text-primary">
                        Reunión con proveedor
                      </p>
                      <p className="text-xs text-text-muted">Mañana, 10:30</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <QuickActionFAB />
    </div>
  );
};

export default Dashboard;